/**
 * Regras puras da página do integrante das Ações ECOA: o semestre (ciclo) inteiro numa leitura, sem seletor de
 * fase. Datas em milissegundos. As regras de edição e exclusão são as da página antiga (`pages/Acao/index.tsx`, que
 * saiu do repositório nesta feature) e do `TaskItemEcoa`, com a fase em que a ação foi cadastrada no lugar da fase escolhida no seletor antigo.
 */
import {
  CicloDoSemestre,
  Entrega,
  FaseDoCiclo,
  faseDaRealizacao,
  faseMensalDe,
  proximoCadastramento,
  separarRealizacoes,
} from 'pages/RealizacaoDestaque/realizacoes-do-semestre';

export { janelaDoCiclo, FASE_NAO_IDENTIFICADA } from 'pages/RealizacaoDestaque/realizacoes-do-semestre';
export type { CicloDoSemestre } from 'pages/RealizacaoDestaque/realizacoes-do-semestre';

/** Fase de `cyclesExtended` com as janelas que a ECOA usa. */
export interface FaseEcoa extends FaseDoCiclo {
  /** Fim do período em que uma ação devolvida ainda pode ser editada (`taskEditable`). */
  date_evaluation?: number;
  evaluation_ecoa_days_step2_start?: number;
  evaluation_ecoa_days_step2_finish?: number;
}

export interface CicloEcoa extends CicloDoSemestre {
  steps?: FaseEcoa[];
}

/** Item de `find/entregas_ecoa` usado pela página (subconjunto). */
export interface AcaoEcoa extends Entrega {
  owner_name?: string;
  description?: string;
  result?: string;
  start?: number;
  end?: number;
  feedback?: {
    _id?: string;
    evaluatorId?: string;
    evaluator_nome_guerra?: string;
    message?: string;
    created?: number;
    etapa?: number;
    /** Gestor original da ação na lateralidade: o feedback conta para ele. */
    original_task_manager?: { playerId?: string };
  }[];
  target?: IntegranteDaAcaoEcoa[];
}

/** Integrante de `target` da ação, com a lotação em `extra`. */
export interface IntegranteDaAcaoEcoa {
  _id: string;
  name?: string;
  gestor?: string;
  extra?: Record<string, any>;
}

/** Fase mensal criada sem o cadastramento de Ações ECOA (a página antiga e o calendário a ignoram). */
export const FASE_SEM_CADASTRO_ECOA = '68af4da8458e527eaa788503';

/** Data de início do 2S2024 (`getNamedDates().ciclo_2S2024`): a ECOA começou nesse ciclo. */
export const INICIO_DA_ECOA = 1730084400000;

/** ECN-02: ciclos com `date_start` a partir do 2S2024, quando a ECOA começou (o rótulo vem do `SeletorDeCiclo`). */
export function ciclosDaEcoa<T extends CicloDoSemestre>(ciclos: T[]): T[] {
  return ciclos.filter(ciclo => ciclo.date_start >= INICIO_DA_ECOA);
}

/** ECN-04 e ECN-05: ações próprias e participações, sem as arquivadas, da mais recente para a mais antiga. */
export function separarAcoes<T extends Entrega>(acoes: T[], chave: string): { minhas: T[]; participacoes: T[] } {
  return separarRealizacoes(acoes, chave) as { minhas: T[]; participacoes: T[] };
}

export interface ContadoresEcoa {
  cadastradas: number;
  participacoes: number;
  aguardando: number;
}

/** ECN-07: "Cadastradas por você", "Participações" e "Aguardando validação" (das duas listas). */
export function contadoresDoSemestre(acoes: Entrega[], chave: string): ContadoresEcoa {
  const { minhas, participacoes } = separarAcoes(acoes, chave);

  return {
    cadastradas: minhas.length,
    participacoes: participacoes.length,
    aguardando: [...minhas, ...participacoes].filter(acao => /aguardando/i.test(acao.status ?? '')).length,
  };
}

/** ECN-06: título da fase mensal (ou sem `type`) em que a ação foi cadastrada. */
export function faseDaAcao(created: number, fases: FaseEcoa[] | undefined): string {
  return faseDaRealizacao(created, fases);
}

/** Fase mensal (ou sem `type`) em que a ação foi cadastrada: a "fase ativa" das regras do cartão. */
export function faseMensalDaAcao(created: number, fases: FaseEcoa[] | undefined): FaseEcoa | undefined {
  return faseMensalDe(created, fases);
}

/**
 * AJ3-31: ações cadastradas na fase mensal escolhida. Sem fase (`''`, "Todos os meses"), todas; a ação fora de
 * qualquer fase mensal só aparece em "Todos os meses".
 */
export function acoesDaFase<T extends { created: number }>(acoes: T[], faseId: string, fases: FaseEcoa[] | undefined): T[] {
  if (!faseId) return acoes;
  return acoes.filter(acao => faseMensalDaAcao(acao.created, fases)?._id === faseId);
}

/** Eixo temático do cartão; "—" quando a ação não tem `indicator.nome`. */
export function eixoDaAcao(acao: Pick<AcaoEcoa, 'indicator'>): string {
  return acao.indicator?.nome || '—';
}

/**
 * Etapa de apreciação da fase como a página antiga calculava (`getActiveEvaluationStep` de `pages/Acao/index.tsx`):
 * só com a fase em `evaluation`, pelas janelas `date_evaluation_step*` (não pelas `evaluation_ecoa_days_*` da fila;
 * pendência registrada na spec).
 */
export function etapaDaFaseDoIntegrante(fase: FaseEcoa | undefined, agora: number): 0 | 2 | 3 | 4 | 4.1 | 5 | null {
  if (!fase || fase.status !== 'evaluation' || !agora) return null;

  const dentro = (inicio?: number, fim?: number) => agora > (inicio as number) && agora < (fim as number);

  if (dentro(fase.date_evaluation_step2_start, fase.date_evaluation_step2_finish)) return 2;
  if (dentro(fase.date_evaluation_step3_start, fase.date_evaluation_step3_finish)) return 3;
  if (dentro(fase.date_evaluation_step4_start, fase.date_evaluation_step4_finish)) return 4;
  if (dentro(fase.date_evaluation_pitStop_start, fase.date_evaluation_pitStop_finish)) return 4.1;
  if (dentro(fase.date_evaluation_step5_start, fase.date_evaluation_step5_finish)) return 5;
  return 0;
}

/** ECN-11: `taskEditable` de `pages/Acao/index.tsx`, com a fase de cadastro da ação como fase ativa. */
export function acaoEditavel(
  acao: Pick<Entrega, 'owner' | 'created' | 'status'>,
  fase: FaseEcoa | undefined,
  chave: string | undefined,
  agora: number,
): boolean {
  if (!fase || !chave || acao.owner !== chave) return false;

  if (acao.status === 'aguardando' && fase.status === 'open' && acao.created >= fase.date_start && acao.created <= fase.date_finish) {
    return true;
  }

  const etapa = etapaDaFaseDoIntegrante(fase, agora);
  if (
    acao.status === 'rejeitado' &&
    etapa &&
    etapa === 2 &&
    acao.created >= fase.date_start &&
    acao.created <= (fase.date_evaluation as number)
  ) {
    return true;
  }

  return false;
}

export type EstadoDoCadastroEcoa =
  /** ECN-23: fase mensal aberta no ciclo selecionado e o usuário pode cadastrar. */
  | { tipo: 'aberto'; fase: FaseEcoa }
  /** ECN-24: fase aberta, mas o usuário não pode cadastrar: prazo sem botão. */
  | { tipo: 'indisponivel'; fase: FaseEcoa }
  /** ECN-26: o cadastramento aberto é o de outro ciclo (o aberto). */
  | { tipo: 'em-outro-ciclo'; ciclo: Pick<CicloDoSemestre, '_id' | 'title'>; fase: FaseEcoa }
  /** ECN-25: nenhuma fase mensal aberta; `proximaAbertura` é o próximo cadastramento do calendário, se houver. */
  | { tipo: 'fechado'; proximaAbertura?: number };

export interface EntradaDoEstadoDoCadastroEcoa {
  ciclos: CicloEcoa[];
  cicloSelecionadoId: string | undefined;
  agora: number;
  /** `userInfo.ecoa === 'INTEGRANTE' && !userInfo.visualizador`. */
  podeCadastrar: boolean;
}

/** ECN-23: integrante que não é só visualizador. */
export function podeCadastrarAcao(userInfo: { ecoa?: string | false; visualizador?: boolean } | null | undefined): boolean {
  return userInfo?.ecoa === 'INTEGRANTE' && !userInfo?.visualizador;
}

function semAFaseSemCadastro(ciclo: CicloEcoa): CicloEcoa {
  return { ...ciclo, steps: (ciclo.steps ?? []).filter(fase => fase._id !== FASE_SEM_CADASTRO_ECOA) };
}

/** ECN-23 a ECN-26: o que o painel mostra sobre o cadastramento. Sem limite mensal: não há estado "já cadastrou". */
export function estadoDoCadastro({ ciclos, cicloSelecionadoId, agora, podeCadastrar }: EntradaDoEstadoDoCadastroEcoa): EstadoDoCadastroEcoa {
  const comCadastro = ciclos.map(semAFaseSemCadastro);
  const cicloAberto = comCadastro.find(ciclo => ciclo.status === 'open');
  const faseAberta = (cicloAberto?.steps ?? []).find(fase => fase.type === 'month' && fase.status === 'open');

  if (!cicloAberto || !faseAberta) {
    return { tipo: 'fechado', proximaAbertura: proximoCadastramento(comCadastro, agora) };
  }

  if (cicloSelecionadoId !== cicloAberto._id) {
    return { tipo: 'em-outro-ciclo', ciclo: { _id: cicloAberto._id, title: cicloAberto.title }, fase: faseAberta };
  }

  return podeCadastrar ? { tipo: 'aberto', fase: faseAberta } : { tipo: 'indisponivel', fase: faseAberta };
}

/** ECN-28: os quatro eixos temáticos do cadastro (`pages/Acao/Create`). */
export const EIXOS_TEMATICOS = ['Autodesenvolvimento', 'Bem-estar', 'Sustentabilidade', 'Cultura'];
